package com.castly.admin.ui.theme

import androidx.compose.runtime.*

/**
 * Centralized design token system for the Castly admin UI.
 * All color values are hex strings compatible with org.jetbrains.compose.web.css.Color().
 */
data class AppColors(
    val background: String,
    val surface: String,
    val surfaceHover: String,
    val primary: String,
    val primaryHover: String,
    val success: String,
    val danger: String,
    val warning: String,
    val info: String,
    val text: String,
    val textMuted: String,
    val border: String,
    val sidebar: String,
    val sidebarText: String,
    val sidebarActive: String,
)

/** Light theme — Duralux-inspired palette */
val LightColors = AppColors(
    background = "#f0f2f8",
    surface = "#ffffff",
    surfaceHover = "#f9fafb",
    primary = "#3454d1",
    primaryHover = "#2a43a7",
    success = "#25b865",
    danger = "#ea4d4d",
    warning = "#ffa21d",
    info = "#3dc7be",
    text = "#283c50",
    textMuted = "#64748b",
    border = "#e5e7eb",
    sidebar = "#ffffff",
    sidebarText = "#283c50",
    sidebarActive = "#f0f2f8",
)

/** Dark theme — Duralux dark mode palette */
val DarkColors = AppColors(
    background = "#121a2d",
    surface = "#0f172a",
    surfaceHover = "#1c2438",
    primary = "#3454d1",
    primaryHover = "#4a6ae0",
    success = "#25b865",
    danger = "#ea4d4d",
    warning = "#ffa21d",
    info = "#3dc7be",
    text = "#b1b4c0",
    textMuted = "#7587a7",
    border = "#1b2436",
    sidebar = "#020617",
    sidebarText = "#cbd5e1",
    sidebarActive = "#1e293b",
)

val LocalAppColors = compositionLocalOf { LightColors }
val LocalIsDark = compositionLocalOf { false }

/** Convenience accessor — use `AppTheme.colors` in any @Composable function. */
object AppTheme {
    val colors: AppColors
        @Composable @ReadOnlyComposable get() = LocalAppColors.current

    val isDark: Boolean
        @Composable @ReadOnlyComposable get() = LocalIsDark.current
}

/** Wraps content in the theme provider. Toggle isDark to switch themes. */
@Composable
fun AppThemeProvider(isDark: Boolean, content: @Composable () -> Unit) {
    val colors = if (isDark) DarkColors else LightColors
    CompositionLocalProvider(
        LocalAppColors provides colors,
        LocalIsDark provides isDark,
    ) {
        content()
    }
}
